import { yup } from '@framework/validation';
import { fromPairs } from 'lodash';
import { getWidgetName } from '../../../../../validator';
import { FormBuilderMetaFlags } from '../../../../../validator/form-builder/flags/FormBuilderMetaFlags';
import { setOptionEnabledCondition } from '../../../../../validator/setEnabledCondition';
import { setOptionVisibleCondition } from '../../../../../validator/setVisibleCondition';
import { setWidgetOptionSchemaLabel } from '../../../../../validator/utils/setWidgetSchemaLabel';
import { isPlainArray } from '../../../../expressions/evaluate/ExpressionValue';
import { evaluateExpression } from '../../../../expressions/evaluate/evaluateExpression';
import { mapReferenceToPath } from '../../../../expressions/evaluate/mapReferenceToPath';
import { createEvaluationVariables } from '../../../../expressions/evaluate/variables/IEvaluationVariables';
import { collectExpressionReferences } from '../../../../expressions/references/collectExpressionReferences';
import { IWidgetOptionValue } from '../../../Widget';
import { isWidgetOptionValue } from '../common/option-list/isWidgetOptionValue';
import { IMultiSelectWidget } from './IMultiSelectWidget';
import { multiSelectOptionsObjectName } from './getMultiSelectOptionsObjectName';

export function getMultiSelectOptionsWidgetSchema(widget: IMultiSelectWidget) {
  const name = getWidgetName(widget);

  const optionsObjectName = multiSelectOptionsObjectName(name);

  const valueOptions = widget.options.filter(isWidgetOptionValue);
  const possibleOptions = valueOptions.map(o => o.value);

  // 1. Schema a lista, in read-only, solo per la registrazione sui
  // `serializedValues` e per l'uso nelle Regole condizionali
  const listSchema = yup
    .array()
    .formBuilderFlags(FormBuilderMetaFlags.SkipDefaultValue)
    .meta({
      [FormBuilderMetaFlags.SerializeSiblingOptions]: possibleOptions,
    })
    .of(yup.string().oneOf(possibleOptions))
    .default(() => [])
    .when([optionsObjectName], ([optionsObjectValue], schema) => {
      return schema.transform(() => {
        // TODO Centralizzare
        const selectedOptions = Object.entries(optionsObjectValue)
          .filter(([key, value]) => value)
          .map(([key, value]) => key);
        return selectedOptions;
      });
    });
  // 2. Schema a oggetto, con una chiave per ogni opzione: utilizzato per
  // il rendering del widget, vedi `CheckboxOptionInput`
  let optionsObjectSchema = yup
    .object(
      fromPairs(
        valueOptions.map(o => [
          o.value,
          createOptionSchema(optionsObjectName, widget, o),
        ]),
      ),
    )
    // In caso di chiavi delle opzioni solo numeriche,
    // il valore dell'optionObject è impostato come un array
    // Applichiamo una transform per assicurarci di avere sempre
    // a che fare con un oggetto, altrimenti yup darà errore in
    // fase di validazione (<code> non è un campo valido -> object != array)
    .transform(value =>
      Array.isArray(value)
        ? fromPairs(
            value
              .map((optionValue, i) => [i.toString(), optionValue])
              .filter(tuple => tuple[1] != null),
          )
        : value,
    )
    .formBuilderFlags(
      FormBuilderMetaFlags.SkipDefaultValue,
      FormBuilderMetaFlags.SkipValidationRules,
    );

  optionsObjectSchema = setMultiSelectDefault(
    widget,
    valueOptions,
    optionsObjectSchema,
  );
  optionsObjectSchema = optionsObjectSchema.addChildrenParentDeps().noUnknown();

  return {
    [name]: listSchema.notRequired(),
    [optionsObjectName]: optionsObjectSchema.notRequired(),
  };
}

/**
 * Imposta il valore di default per un multi-select widget, ovvero passando
 * da `['A', 'B']` a `{ A: true, B: true, C: false }`.
 */
function setMultiSelectDefault(
  widget: IMultiSelectWidget,
  valueOptions: IWidgetOptionValue[],
  schema: yup.ObjectSchema<any>,
) {
  if (widget.defaultValue == null) return schema;

  const expression = widget.defaultValue;

  const references = collectExpressionReferences(expression);
  const paths = references.map(r => mapReferenceToPath(r));

  return schema.parentWhen(
    paths,
    (values, schema: yup.AnySchema, context: any) => {
      const variables = createEvaluationVariables(references, values);
      const result = evaluateExpression(expression, { variables });

      const defaultValue = fromPairs(valueOptions.map(o => [o.value, false]));

      if (result != null && Array.isArray(result) && isPlainArray(result)) {
        for (const defaultOption of result) {
          defaultValue[defaultOption] = true;
        }
      }

      return schema.default(defaultValue);
    },
  );
}

function createOptionSchema(
  parentName: string,
  widget: IMultiSelectWidget,
  option: IWidgetOptionValue,
) {
  let schema = yup.boolean().notRequired();
  schema = setOptionEnabledCondition(schema, widget, option);
  schema = setOptionVisibleCondition(schema, widget, option);
  schema = setWidgetOptionSchemaLabel(option, schema);
  return schema;
}
